import React, { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Pagination from '@mui/material/Pagination'
import Collapse from '@mui/material/Collapse'
import DownloadIcon from '@mui/icons-material/Download'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { formatDate } from '@/v2/utils/dateHelpers'
import {
  DataTable,
  TableHeader,
  TableHeaderActions,
  Table,
  TableHead,
  TableRow,
  TableHeaderCell,
  TableCell,
  Badge,
} from '../styles'

const DailyPerformanceTable = ({ data, onExport, onPageChange }) => {
  const [expanded, setExpanded] = useState(true)

  const dailyData = data?.data || []
  const pagination = data?.pagination || {
    current_page: 1,
    per_page: 10,
    total_pages: 1,
    total_items: 0,
    has_next: false,
    has_prev: false,
  }

  // The page is driven by the server response (fetched by the parent), not by
  // local state, so it stays in sync when filters reset the page upstream.
  const currentPage = pagination.current_page

  const handlePageChange = (event, page) => {
    if (onPageChange) {
      onPageChange(page)
    }
  }

  const formatCurrency = value => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  const headers = [
    { label: 'Date' },
    { label: 'Day' },
    { label: 'Bookings', tooltip: 'Total appointments scheduled' },
    { label: 'Showed', tooltip: 'Patients who attended' },
    { label: 'No-Shows', tooltip: 'Patients who missed appointments' },
    { label: 'Show Rate', tooltip: 'Percentage of patients who showed up' },
    { label: 'Starts', tooltip: 'Patients who began treatment' },
    { label: 'Revenue', tooltip: 'Total revenue generated' },
    { label: 'Conv. Rate', tooltip: 'Conversion rate from bookings to starts' },
  ]

  return (
    <DataTable elevation={0}>
      <TableHeader
        sx={{
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme => theme.palette.primary?.dark || '#0056b3',
          },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            size="small"
            sx={{
              color: 'white',
              p: 0.5,
            }}
            onClick={e => {
              e.stopPropagation()
              setExpanded(!expanded)
            }}
          >
            {expanded ? (
              <ExpandLessIcon style={{ color: 'white' }} />
            ) : (
              <ExpandMoreIcon style={{ color: 'white' }} />
            )}
          </IconButton>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Daily Performance
            </Typography>
            <Typography
              variant="body2"
              sx={theme => ({
                color: theme.palette.common?.white || 'white',
                opacity: 0.8,
                mt: 0.5,
              })}
            >
              Day-by-day breakdown of appointments, attendance, starts, and
              revenue
            </Typography>
          </Box>
        </Box>
        <TableHeaderActions onClick={e => e.stopPropagation()}>
          <Typography
            variant="body2"
            sx={theme => ({
              color: theme.palette.common?.white || 'white',
              opacity: 0.9,
              fontWeight: 500,
              mr: 2,
            })}
          >
            {pagination.total_items || 0} days
          </Typography>
          <Tooltip title="Export to CSV">
            <IconButton
              size="small"
              onClick={onExport}
              sx={{
                color: 'white',
                '&:hover': {
                  backgroundColor: '#ffffff1a',
                },
              }}
            >
              <DownloadIcon fontSize="small" style={{ color: 'white' }} />
            </IconButton>
          </Tooltip>
        </TableHeaderActions>
      </TableHeader>

      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {dailyData && dailyData.length > 0 ? (
          <>
            <Box sx={{ overflowX: 'auto' }}>
              <Table>
                <TableHead>
                  <tr>
                    {headers.map((header, index) => (
                      <TableHeaderCell key={index}>
                        {header.tooltip ? (
                          <Tooltip title={header.tooltip}>
                            <span>{header.label}</span>
                          </Tooltip>
                        ) : (
                          header.label
                        )}
                      </TableHeaderCell>
                    ))}
                  </tr>
                </TableHead>
                <tbody>
                  {dailyData.map((day, index) => (
                    <TableRow key={`${day.date}-${index}`}>
                      <TableCell>
                        {formatDate(day.date, 'MMM dd, yyyy')}
                      </TableCell>
                      <TableCell>{day.day_name}</TableCell>
                      <TableCell>{day.bookings}</TableCell>
                      <TableCell>{day.showed_up}</TableCell>
                      <TableCell>{day.no_shows}</TableCell>
                      <TableCell>
                        <Badge
                          label={`${day.show_rate}%`}
                          color={
                            day.show_rate >= 80
                              ? 'success'
                              : day.show_rate >= 60
                              ? 'warning'
                              : 'error'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Badge
                          label={day.starts}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <span style={{ fontWeight: 600, color: '#05AD48' }}>
                          {formatCurrency(day.revenue)}
                        </span>
                      </TableCell>
                      <TableCell>
                        <Badge
                          label={`${day.conversion_rate}%`}
                          color={
                            day.conversion_rate >= 30
                              ? 'success'
                              : day.conversion_rate >= 15
                              ? 'warning'
                              : 'error'
                          }
                          size="small"
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </tbody>
              </Table>
            </Box>

            {pagination.total_pages > 1 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  p: 2,
                  borderTop: '1px solid',
                  borderColor: theme => theme.palette.divider || '#e0e0e0',
                  backgroundColor: '#f9fafb',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', fontSize: '14px' }}
                >
                  Showing{' '}
                  {(pagination.current_page - 1) * pagination.per_page + 1} to{' '}
                  {Math.min(
                    pagination.current_page * pagination.per_page,
                    pagination.total_items
                  )}{' '}
                  of {pagination.total_items} days
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <IconButton
                    onClick={() => handlePageChange(null, currentPage - 1)}
                    disabled={!pagination.has_prev}
                    size="small"
                    sx={{
                      color: 'text.secondary',
                      '&:disabled': {
                        opacity: 0.3,
                      },
                    }}
                  >
                    <ChevronLeftIcon />
                  </IconButton>

                  <Pagination
                    count={pagination.total_pages}
                    page={currentPage}
                    onChange={handlePageChange}
                    size="small"
                    siblingCount={1}
                    boundaryCount={1}
                    sx={{
                      '& .MuiPaginationItem-root': {
                        fontSize: '14px',
                        minWidth: '32px',
                        height: '32px',
                        borderRadius: '8px',
                      },
                      '& .Mui-selected': {
                        backgroundColor: theme =>
                          theme.palette.primary?.main || '#0074FE',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: theme =>
                            theme.palette.primary?.dark || '#0056b3',
                        },
                      },
                    }}
                  />

                  <IconButton
                    onClick={() => handlePageChange(null, currentPage + 1)}
                    disabled={!pagination.has_next}
                    size="small"
                    sx={{
                      color: 'text.secondary',
                      '&:disabled': {
                        opacity: 0.3,
                      },
                    }}
                  >
                    <ChevronRightIcon />
                  </IconButton>
                </Box>
              </Box>
            )}
          </>
        ) : (
          <Box sx={{ padding: '48px', textAlign: 'center', color: '#6b7280' }}>
            No daily data available for the selected date range
          </Box>
        )}
      </Collapse>
    </DataTable>
  )
}

export default DailyPerformanceTable
